import { documentStore, uiStore } from "@/store/store";

type MessageRequest = {
  id: string;
  type: "REQUEST";
  payload: any;
};

type MessageResponse<T = any> = {
  id: string;
  type: "RESPONSE";
  payload: {
    success: boolean;
    payload?: T;
    error?: string;
  };
};

export type MessageType =
  | "Section imported"
  | "Element activated"
  | "Section prompt updated"
  | "Section shuffled";

let iframeRef: HTMLIFrameElement | null = null;

const pendingRequests = new Map<
  string,
  (response: MessageResponse["payload"]) => void
>();

function generateId(): string {
  return `msg_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

if (typeof window !== "undefined") {
  window.addEventListener("message", (event: MessageEvent) => {
    const data = event.data;

    if (data?.type === "RESPONSE" && data?.id && pendingRequests.has(data.id)) {
      const resolver = pendingRequests.get(data.id);
      if (resolver) {
        resolver(data.payload);
        manageResponsePayload(data.payload);
        pendingRequests.delete(data.id);
      }
    } else if (data?.type === "RESPONSE") {
      manageResponsePayload(data.payload);
    }
  });
}

function manageResponsePayload(payload: MessageResponse["payload"]): void {
  if (payload.payload) {
    documentStore.setDocument(payload.payload.document);
    uiStore.activateElementMutation(payload.payload.activeElementId);
  }
}

export default {
  set(ref: HTMLIFrameElement): void {
    iframeRef = ref;
  },

  get(): HTMLIFrameElement | null {
    return iframeRef;
  },

  clear(): void {
    iframeRef = null;
  },

  async sendRequest<T = any>(
    message: Omit<MessageRequest, "id" | "type">,
    targetOrigin = "*"
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (!iframeRef?.contentWindow) {
        resolve(null as unknown as T);
        return;
      }

      const id = generateId();
      const fullMessage: MessageRequest = {
        id,
        type: "REQUEST",
        payload: message.payload
      };

      const timeout = setTimeout(() => {
        pendingRequests.delete(id);
        // reject(new Error("Iframe response timeout"));
      }, 15000);

      pendingRequests.set(id, responsePayload => {
        clearTimeout(timeout);

        if (responsePayload.success) {
          resolve(responsePayload.payload as T);
        } else {
          reject(new Error(responsePayload.error || "Unknown iframe error"));
        }
      });

      iframeRef.contentWindow.postMessage(fullMessage, targetOrigin);
    });
  }
};
